import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { X, Loader, Skull } from "lucide-react";
import { toast } from "sonner";

export const SaveSlots = ({ open, onClose }) => {
  const { refreshState } = useAuth();
  const [slots, setSlots] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(null); // "save-1" | "load-1" | ...
  const [confirmLoad, setConfirmLoad] = useState(null);

  const fetchSlots = useCallback(async () => {
    try {
      const { data } = await api.get("/game/slots");
      setSlots(data.slots);
    } catch {
      toast.error("The crypt door is stuck.");
    }
  }, []);

  useEffect(() => {
    if (open) { setConfirmLoad(null); fetchSlots(); }
  }, [open, fetchSlots]);

  if (!open) return null;

  const save = async (slot) => {
    setBusy(`save-${slot}`);
    try {
      const { data } = await api.post(`/game/slots/${slot}/save`, { name });
      toast.success(`Horde entombed as "${data.name}".`);
      setName("");
      await fetchSlots();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(null);
    }
  };

  const load = async (slot) => {
    if (confirmLoad !== slot) { setConfirmLoad(slot); return; }
    setBusy(`load-${slot}`);
    try {
      const { data } = await api.post(`/game/slots/${slot}/load`);
      toast.success(`"${data.name}" rises. Your current horde is replaced.`);
      setConfirmLoad(null);
      await refreshState();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: "rgba(3,3,3,0.85)", backdropFilter: "blur(6px)" }}
         data-testid="save-slots-modal">
      <div className="w-full max-w-xl bg-[#0A0A0C] border border-[var(--border-blood)] p-6 sm:p-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="tac-label">THE CRYPT</p>
            <h2 className="font-serif-h text-3xl uppercase text-bone mt-1">Save Slots</h2>
            <p className="text-xs text-muted-h mt-2 leading-relaxed max-w-sm">
              Entomb your current horde, or raise a buried one. Loading REPLACES your live game.
            </p>
          </div>
          <button onClick={onClose} className="btn-ghost !p-2" data-testid="save-slots-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="mb-5">
          <label className="tac-label">NAME FOR NEW ENTOMBMENT (OPTIONAL)</label>
          <input type="text" value={name} maxLength={40}
                 onChange={(e) => setName(e.target.value)}
                 placeholder="e.g. THE CAUTIOUS ONE"
                 className="brutal-input" data-testid="save-slot-name-input" />
        </div>

        {!slots ? (
          <p className="text-sm text-muted-h flex items-center gap-2">
            <Loader className="w-3 h-3 animate-spin" /> Opening the crypt…
          </p>
        ) : (
          <div className="space-y-3">
            {slots.map((s) => (
              <div key={s.slot}
                   className="border border-[var(--border-subtle)] p-4 flex flex-wrap items-center gap-3"
                   data-testid={`save-slot-${s.slot}`}>
                <Skull className={`w-4 h-4 ${s.empty ? "text-muted-h/40" : "text-blood"}`} />
                <div className="flex-1 min-w-[140px]">
                  {s.empty ? (
                    <p className="font-mono text-xs text-muted-h/60 tracking-widest">EMPTY CRYPT {s.slot}</p>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[0.55rem] tracking-widest border border-blood text-blood px-1.5 py-0.5 shrink-0"
                              data-testid={`save-slot-${s.slot}-rank-badge`}>
                          R{(s.rank_index ?? 0) + 1}
                        </span>
                        <p className="font-serif-h text-lg text-bone leading-tight">{s.name}</p>
                      </div>
                      <p className="font-mono text-[0.6rem] text-muted-h tracking-widest mt-0.5">
                        {s.rank_name?.toUpperCase()} · {s.personal_horde.toLocaleString()} HORDE · {s.battles_won} WINS
                        {typeof s.world_pct === "number" && (
                          <span className="text-blood/80" data-testid={`save-slot-${s.slot}-world-pct`}>
                            {" "}· {s.world_pct}% WORLD DEAD
                          </span>
                        )}
                      </p>
                    </>
                  )}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => save(s.slot)} disabled={!!busy}
                          className="btn-ghost text-[0.65rem] !px-3 !py-2"
                          data-testid={`save-slot-${s.slot}-save`}>
                    {busy === `save-${s.slot}` ? <Loader className="w-3 h-3 animate-spin" /> : "ENTOMB"}
                  </button>
                  {!s.empty && (
                    <button onClick={() => load(s.slot)} disabled={!!busy}
                            className={confirmLoad === s.slot ? "btn-blood text-[0.65rem] !px-3 !py-2" : "btn-bone text-[0.65rem] !px-3 !py-2"}
                            data-testid={`save-slot-${s.slot}-load`}>
                      {busy === `load-${s.slot}` ? <Loader className="w-3 h-3 animate-spin" />
                        : confirmLoad === s.slot ? "SURE? REPLACES LIVE GAME" : "RISE"}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SaveSlots;
